import { useCallback } from "react";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { usePatient } from "../state/patientContext";
import { isProduction } from "fe-common/utils/envGetter";
import { useConversationDriver } from "../driver/ConversationDriverProvider";
import { useClinicData } from "../clinic/clinicHooks";
import { useQueryParams } from "fe-common/useQueryParams";

const CONVERSATION_STORAGE_KEY = "temp:conversation";

export const CONVERSATION_SAVE_TYPE = "conversation";

const buildLinks = (caseId: string, clinic: string, lang: string) => {
  const clinicSettings = `clinic/${clinic}/`;

  if (isProduction()) {
    return {
      url: `https://diagnose.kahun.com/clinic_manage/${clinic}/patientCard?shareId=${caseId}`,
      shareId: caseId,
      reportUrl: `https://editor.kahun.com/reports?RunConv:${caseId}`,
      explorerUrl: `https://editor.kahun.com/#/w-kahun/wcase/${caseId}`,
      explorerUrl_v2: `https://editor.kahun.com/#/w-kahun/wconversationcase/${caseId}`,
      agentUrl: `https://diagnose.kahun.com/agent/patientFirstSummary?shareId=${caseId}`,
      conversationUrl: `https://patient.kahun.com/${clinicSettings}?ksrc=patient_copy&patientCaseId=${caseId}&lang=${lang ||
        "en"}`,
    };
  } else {
    return {
      url: `https://develop.kahun.com/clinic_manage/${clinic}/patientCard?shareId=${caseId}`,
      shareId: caseId,
      reportUrl: `https://editor-dev.kahun.com/#/w-kahun/reports?RunConv:${caseId}`,
      explorerUrl: `https://editor-dev.kahun.com/#/w-kahun/wcase/${caseId}`,
      explorerUrl_v2: `https://editor-dev.kahun.com/#/w-kahun/wconversationcase/${caseId}`,
      agentUrl: `https://develop.kahun.com/agent/patientFirstSummary?shareId=${caseId}`,
      conversationUrl: `https://patient-dev.kahun.com/${clinicSettings}?ksrc=patient_copy&patientCaseId=${caseId}&lang=${lang ||
        "en"}`,
    };
  }
};

export const useGetProviderLink = () => {
  const { clinic } = useClinicData();
  const { caseId } = usePatient();
  const links = buildLinks(caseId, clinic, "");
  return links.url;
};

export const useSaveConversationAsCase = () => {
  const dao = useDaoContext();
  const driver = useConversationDriver();
  const { clinic } = useClinicData();
  const queryParams = useQueryParams();
  const lang = queryParams.get("lang");

  return useCallback(
    async (
      readOnly: boolean = true,
    ): Promise<{
      url: string;
      shareId: string;
      reportUrl: string;
      explorerUrl: string;
      explorerUrl_v2: string;
      agentUrl: string;
      conversationUrl: string;
    }> => {
      let caseId = await dao.saveConversationAsNew(
        driver.getCurrentContext(),
        readOnly,
      );
      return buildLinks(caseId, clinic, lang);
    },
    [dao, driver, clinic, lang],
  );
};

export const useSaveConversationInStorage = () => {
  const driver = useConversationDriver();
  const { exchanges, loading } = usePatient();

  return useCallback(
    (sessionId = null) => {
      const storedState = {
        version: "3.0.0",
        context: driver.getCurrentContext(),
      };
      // Make sure to write when there is data and state was initilized
      if (!loading && exchanges?.length) {
        try {
          sessionStorage.setItem(
            (sessionId || "") + CONVERSATION_STORAGE_KEY,
            JSON.stringify(storedState),
          );
        } catch (e) {
          console.error(e);
        }
      }
    },
    [driver, loading, exchanges],
  );
};

export const restoreConversationFromStorage = (sessionId?: string) => {
  const storageValue = sessionStorage.getItem(
    (sessionId || "") + CONVERSATION_STORAGE_KEY,
  );
  //sessionStorage.removeItem(sessionId || "" + CONVERSATION_STORAGE_KEY);
  if (!storageValue || storageValue === "") return null;
  return JSON.parse(storageValue);
};
